import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Activity } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { PartyPopper, PlusCircle, ThumbsUp, ThumbsDown, Trash2, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/Button';

function Section({
  title,
  count,
  isOpen,
  onToggle,
  children,
}: { title: string; count: number; isOpen: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-2 text-left transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
      >
        {isOpen ? <ChevronUp size={14} className="text-slate-400 dark:text-slate-500 shrink-0" /> : <ChevronDown size={14} className="text-slate-400 dark:text-slate-500 shrink-0" />}
        <h2 className="text-ds-meta font-medium">{title}</h2>
        <span className="text-ds-meta text-slate-300 dark:text-slate-600">{count}</span>
      </button>
      {isOpen && (
        <div className="mt-3">
          {count === 0 ? (
            <p className="text-ds-body text-slate-400 dark:text-slate-500 py-4">Žádné aktivity</p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}

function ActivityCard({
  activity,
  t,
  onEdit,
  onDelete,
  onVote,
}: {
  activity: Activity;
  t: (key: string) => string;
  onEdit: () => void;
  onDelete: () => void;
  onVote: (id: string, direction: 'up' | 'down') => void;
}) {
  const typeLabel = activity.type === 'planned' ? t('entertainment.form.planned') : t('entertainment.form.proposal');
  const statusLabel = t(`entertainment.status.${activity.status}`);
  const statusStyles =
    activity.status === 'approved'
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
      : activity.status === 'rejected'
        ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
        : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';

  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-all group">
      <div className="flex justify-between items-start gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          <span
            className={`px-2.5 py-1 rounded-full text-ds-meta font-medium ${
              activity.type === 'planned' ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400' : 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
            }`}
          >
            {typeLabel}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-ds-meta font-medium ${statusStyles}`}>
            {statusLabel}
          </span>
        </div>
        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button type="button" onClick={onEdit} className="p-1.5 text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800" aria-label={t('common.edit')}>
            <Edit2 size={16} />
          </button>
          <button type="button" onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800" aria-label={t('common.delete')}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      <h3 className="text-ds-h3 font-semibold text-slate-900 dark:text-white mb-2">{activity.title}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 line-clamp-2">{activity.description ?? ''}</p>
      <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="text-ds-meta text-slate-400">
          {activity.is_all_day ? 'Celý den' : (activity.start_time ? `Start: ${activity.start_time.slice(0, 5)}` : 'Čas neurčen')}
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
          <button
            type="button"
            onClick={() => onVote(activity.id, 'up')}
            className="flex items-center gap-1.5 font-medium text-slate-500 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-lg p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800"
            aria-label="Hlasovat pro"
          >
            <ThumbsUp size={16} className="text-green-500" /> {activity.votes_up}
          </button>
          <button
            type="button"
            onClick={() => onVote(activity.id, 'down')}
            className="flex items-center gap-1.5 font-medium text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800"
            aria-label="Hlasovat proti"
          >
            <ThumbsDown size={16} className="text-red-500" /> {activity.votes_down}
          </button>
        </div>
      </div>
    </div>
  );
}

const INITIAL_ACTIVITY: Partial<Activity> = {
  title: '',
  description: '',
  type: 'planned',
  status: 'draft',
  votes_up: 0,
  votes_down: 0,
  is_all_day: false
};

export default function Entertainment() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [activities, setActivities] = useState<Activity[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Activity>>(INITIAL_ACTIVITY);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [plannedOpen, setPlannedOpen] = useState(true);
  const [proposalsOpen, setProposalsOpen] = useState(true);

  const plannedActivities = activities.filter(a => a.type === 'planned');
  const proposalActivities = activities.filter(a => a.type === 'proposal');

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchActivities = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('activities').select('*').eq('wedding_id', wedding.id);
    if (data) setActivities(data);
  };

  useEffect(() => {
    fetchActivities();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    if (formData.id) {
      const { error } = await supabase.from('activities').update(formData).eq('id', formData.id);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    } else {
      const { error } = await supabase.from('activities').insert({ ...formData, wedding_id: wedding.id });
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchActivities();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('activities').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchActivities();
  };

  const handleVote = async (activityId: string, direction: 'up' | 'down') => {
    const activity = activities.find(a => a.id === activityId);
    if (!activity) return;
    const newUp = activity.votes_up + (direction === 'up' ? 1 : 0);
    const newDown = activity.votes_down + (direction === 'down' ? 1 : 0);
    setActivities(prev => prev.map(a =>
      a.id === activityId ? { ...a, votes_up: newUp, votes_down: newDown } : a
    ));
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('activities').update({ votes_up: newUp, votes_down: newDown }).eq('id', activityId);
    if (error) {
      showToast('error', `Chyba: ${error.message}`);
      fetchActivities();
    }
  };

  if (!wedding) return null;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('entertainment.title')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">{t('entertainment.subtitle')}</p>
        </div>
        <Button 
          onClick={() => { setFormData(INITIAL_ACTIVITY); setIsModalOpen(true); }}
          variant="secondary"
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      {activities.length === 0 ? (
        <EmptyState
          icon={PartyPopper}
          title="Žádná zábava"
          description="Naplánujte hry a aktivity pro hosty."
          action={{ label: t('common.add'), onClick: () => { setFormData(INITIAL_ACTIVITY); setIsModalOpen(true); } }}
        />
      ) : (
        <>
          <Section
            title={t('entertainment.form.planned')}
            count={plannedActivities.length}
            isOpen={plannedOpen}
            onToggle={() => setPlannedOpen(v => !v)}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {plannedActivities.map(activity => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  t={t}
                  onEdit={() => { setFormData(activity); setIsModalOpen(true); }}
                  onDelete={() => setDeleteId(activity.id)}
                  onVote={handleVote}
                />
              ))}
            </div>
          </Section>
          <Section
            title={t('entertainment.form.proposal')}
            count={proposalActivities.length}
            isOpen={proposalsOpen}
            onToggle={() => setProposalsOpen(v => !v)}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {proposalActivities.map(activity => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  t={t}
                  onEdit={() => { setFormData(activity); setIsModalOpen(true); }}
                  onDelete={() => setDeleteId(activity.id)}
                  onVote={handleVote}
                />
              ))}
            </div>
          </Section>
        </>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit aktivitu' : 'Nová aktivita'}>
         <form onSubmit={handleSave} className="space-y-6">
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">{t('entertainment.form.title')}</label>
               <input 
                  type="text" 
                  required
                  value={formData.title || ''} 
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
               />
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">{t('entertainment.form.desc')}</label>
               <textarea 
                  value={formData.description || ''} 
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
               />
            </div>
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">{t('entertainment.form.type')}</label>
                  <select 
                     value={formData.type} 
                     onChange={e => setFormData({...formData, type: e.target.value as any})}
                     className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
                  >
                     <option value="planned">{t('entertainment.form.planned')}</option>
                     <option value="proposal">{t('entertainment.form.proposal')}</option>
                  </select>
               </div>
               <div>
                  <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.status')}</label>
                  <select 
                     value={formData.status} 
                     onChange={e => setFormData({...formData, status: e.target.value as any})}
                     className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
                  >
                     <option value="draft">{t('entertainment.status.draft')}</option>
                     <option value="approved">{t('entertainment.status.approved')}</option>
                     <option value="rejected">{t('entertainment.status.rejected')}</option>
                  </select>
               </div>
            </div>
            <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 cursor-pointer">
               <input 
                  type="checkbox" 
                  checked={formData.is_all_day} 
                  onChange={e => setFormData({...formData, is_all_day: e.target.checked})}
                  className="w-5 h-5 rounded text-brand-600 focus:ring-brand-500"
               />
               <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{t('entertainment.form.all_day')}</span>
            </label>
            <button type="submit" className="w-full bg-brand-600 text-white font-bold py-4 rounded-xl hover:bg-brand-700 transition-colors">
               {t('common.save')}
            </button>
         </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}